import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Send, Edit2, History } from "lucide-react";
import BSPeriodPicker from "@/components/BSPeriodPicker";

/**
 * Phase 3 — Financial Maker-Checker (Product Manager desktop).
 *
 * One screen, 5 tabs (one per kind). Each tab shows a list of proposals with
 * status pills, a "Create draft" button, an Edit button (drafts only), and a
 * "Submit for approval" button (drafts only). Approved + active rules show up
 * with a green badge — these are what the Phase 4 calc engine will consume.
 */
const KINDS = [
  { key: "incentive_rule", label: "Incentive Rules" },
  { key: "target", label: "Targets" },
  { key: "slab", label: "Slabs" },
  { key: "bonus_rule", label: "Bonus Rules" },
  { key: "scheme", label: "Schemes" },
];

export default function FinancialRules() {
  const [kind, setKind] = useState("incentive_rule");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [editing, setEditing] = useState(null);   // {kind, record?} record absent => create
  const [viewingAudit, setViewingAudit] = useState(null); // record

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const { data } = await api.get(`/promoter/financial/${kind}`, { params });
      setRows(data || []);
    } catch (e) {
      toast.error("Could not load", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [kind, statusFilter]);

  const submit = async (rid) => {
    try {
      await api.post(`/promoter/financial/${kind}/${rid}/submit`);
      toast.success("Submitted for Admin approval");
      load();
    } catch (e) {
      toast.error("Could not submit", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };

  return (
    <div className="space-y-4" data-testid="financial-rules-page">
      <header className="flex justify-between items-center flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Financial Rules</h1>
          <p className="text-sm text-slate-500 mt-1">
            Propose new rules / targets / slabs / bonuses / schemes. Approved by Admin before they affect any calculation.
          </p>
        </div>
        <Button onClick={() => setEditing({ kind })}
                className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="fin-new-btn">
          <Plus size={14} className="mr-1" /> New {KINDS.find((k) => k.key === kind)?.label?.slice(0, -1)}
        </Button>
      </header>

      <div className="flex gap-1 border-b border-slate-200" data-testid="fin-tabs">
        {KINDS.map((k) => (
          <button key={k.key} onClick={() => setKind(k.key)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${kind === k.key ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}
                  data-testid={`fin-tab-${k.key}`}>
            {k.label}
          </button>
        ))}
      </div>

      <div className="flex gap-2 items-end">
        <div>
          <Label className="text-[11px] uppercase font-semibold text-slate-600">Filter</Label>
          <Select value={statusFilter || "__all__"} onValueChange={(v) => setStatusFilter(v === "__all__" ? "" : v)}>
            <SelectTrigger className="h-8 w-52" data-testid="fin-status-filter"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All</SelectItem>
              <SelectItem value="draft">Drafts</SelectItem>
              <SelectItem value="pending_admin_approval">Pending</SelectItem>
              <SelectItem value="approved">Approved (Active)</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="fin-table">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2">Created</th>
                  <th className="text-left px-3 py-2">Summary</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Created By</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={5} className="text-center py-6 text-slate-400">Loading…</td></tr>}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-6 text-slate-400" data-testid="fin-empty">
                    No {KINDS.find((k) => k.key === kind)?.label?.toLowerCase()} yet.
                  </td></tr>
                )}
                {!loading && rows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100"
                      data-testid={`fin-row-${r.id.slice(0, 8)}`}>
                    <td className="px-3 py-2 text-xs font-mono">{(r.created_at || "").slice(0, 10)}</td>
                    <td className="px-3 py-2 max-w-md break-words">
                      <ProposalSummary kind={kind} payload={r.payload} />
                      {r.rejection_reason && (
                        <div className="text-[10px] text-rose-700 mt-1">Rejected: {r.rejection_reason}</div>
                      )}
                    </td>
                    <td className="px-3 py-2"><FinStatusPill status={r.status} active={r.active} /></td>
                    <td className="px-3 py-2 text-xs">{r.created_by || "—"}</td>
                    <td className="px-3 py-2 text-right space-x-1">
                      {r.status === "draft" && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => setEditing({ kind, record: r })}
                                  data-testid={`fin-edit-${r.id.slice(0, 8)}`}>
                            <Edit2 size={12} />
                          </Button>
                          <Button size="sm" onClick={() => submit(r.id)}
                                  className="bg-slate-900 hover:bg-slate-800 text-white"
                                  data-testid={`fin-submit-${r.id.slice(0, 8)}`}>
                            <Send size={12} className="mr-1" /> Submit
                          </Button>
                        </>
                      )}
                      <Button size="sm" variant="outline" onClick={() => setViewingAudit(r)}
                              data-testid={`fin-audit-${r.id.slice(0, 8)}`}>
                        <History size={12} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <ProposalDialog state={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      <AuditDialog record={viewingAudit} onClose={() => setViewingAudit(null)} />
    </div>
  );
}

function ProposalSummary({ kind, payload }) {
  if (!payload) return <span className="text-slate-400">—</span>;
  switch (kind) {
    case "incentive_rule":
      return <span><b>{payload.brand}</b>{payload.product_category ? ` · ${payload.product_category}` : ""}{payload.model ? ` · ${payload.model}` : ""} → <b>Rs. {payload.incentive_amount}</b>/unit</span>;
    case "target":
      return <span>{payload.bs_period_label ? `${payload.bs_period_label} (BS) · ` : ""}{payload.month_year} · Promoter <b>{payload.promoter_id?.slice(0, 8)}…</b> · Rs. {Number(payload.target_sales_amount).toLocaleString()}{payload.target_quantity ? ` · ${payload.target_quantity} units` : ""}</span>;
    case "slab":
      return <span>{payload.achievement_pct_from}%{payload.achievement_pct_to ? `–${payload.achievement_pct_to}%` : "+"} → <b>{payload.payout_pct}%</b> payout{payload.bonus_type && payload.bonus_type !== "none" ? ` + ${payload.bonus_type} ${payload.bonus_value}` : ""}</span>;
    case "bonus_rule":
      return <span><b>{payload.name}</b> · Rs. {payload.bonus_amount}</span>;
    case "scheme":
      return <span><b>{payload.name}</b> · {payload.target_type} · {payload.start_date}→{payload.end_date} · Rs. {Number(payload.target_amount).toLocaleString()}</span>;
    default:
      return <span>{JSON.stringify(payload)}</span>;
  }
}

function FinStatusPill({ status, active }) {
  const map = {
    draft: "bg-slate-200 text-slate-700",
    pending_admin_approval: "bg-amber-100 text-amber-800",
    approved: "bg-emerald-100 text-emerald-800",
    rejected: "bg-rose-100 text-rose-800",
    inactive: "bg-slate-300 text-slate-700",
  };
  const label = status === "approved" && active ? "approved · active"
              : status === "approved" && !active ? "approved · inactive"
              : (status || "").replace(/_/g, " ");
  return <Badge className={map[status] || "bg-slate-100 text-slate-700"}>{label}</Badge>;
}

function ProposalDialog({ state, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!state) return;
    setForm(state.record?.payload || defaultPayload(state.kind));
    setRemarks(state.record?.remarks || "");
  }, [state]);

  if (!state) return null;
  const { kind, record } = state;
  const isEdit = !!record;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      if (isEdit) {
        await api.put(`/promoter/financial/${kind}/${record.id}`, { payload: form, remarks });
        toast.success("Draft updated");
      } else {
        await api.post(`/promoter/financial/${kind}`, { payload: form, remarks });
        toast.success("Draft created");
      }
      onSaved();
    } catch (e) {
      toast.error("Could not save", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid={`fin-dialog-${kind}`}>
        <DialogHeader><DialogTitle>{isEdit ? "Edit" : "New"} {kind.replace("_", " ")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <KindForm kind={kind} form={form} update={update} />
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Remarks</Label>
            <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="fin-remarks" />
          </div>
          <p className="text-xs text-slate-500">
            This will be saved as a <b>draft</b>. It has no effect on calculations until you submit it and Admin approves it.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="fin-dialog-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy}
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="fin-dialog-save">
            {busy ? "Saving..." : (isEdit ? "Save draft" : "Create draft")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function defaultPayload(kind) {
  switch (kind) {
    case "incentive_rule": return { brand: "", product_category: "", model: "", incentive_amount: "", effective_from: "", effective_to: "" };
    case "target": return { promoter_id: "", bs_year: "", bs_month: "", month_year: "", target_sales_amount: "", target_quantity: "" };
    case "slab": return { achievement_pct_from: "", achievement_pct_to: "", payout_pct: "", bonus_type: "none", bonus_value: "" };
    case "bonus_rule": return { name: "", description: "", bonus_amount: "" };
    case "scheme": return { name: "", target_type: "monthly", start_date: "", end_date: "", target_amount: "", target_quantity: "" };
    default: return {};
  }
}

function KindForm({ kind, form, update }) {
  if (kind === "incentive_rule") return (
    <div className="grid grid-cols-2 gap-2">
      <F label="Brand" required><Input value={form.brand || ""} onChange={(e) => update("brand", e.target.value)} data-testid="fin-brand" /></F>
      <F label="Product Category"><Input value={form.product_category || ""} onChange={(e) => update("product_category", e.target.value)} /></F>
      <F label="Model (optional, most specific)"><Input value={form.model || ""} onChange={(e) => update("model", e.target.value)} /></F>
      <F label="Incentive Amount (NPR/unit)" required><Input type="number" value={form.incentive_amount || ""} onChange={(e) => update("incentive_amount", e.target.value)} data-testid="fin-amount" /></F>
      <F label="Effective From"><Input type="date" value={form.effective_from || ""} onChange={(e) => update("effective_from", e.target.value)} /></F>
      <F label="Effective To (optional)"><Input type="date" value={form.effective_to || ""} onChange={(e) => update("effective_to", e.target.value)} /></F>
    </div>
  );
  if (kind === "target") return (
    <div className="space-y-3">
      <BSPeriodPicker
        bsYear={form.bs_year} bsMonth={form.bs_month}
        onChange={({ bs_year, bs_month, bs_month_name, ad_start, ad_end, ad_month_year, bs_period_label }) => {
          update("bs_year", bs_year);
          update("bs_month", bs_month);
          update("bs_month_name", bs_month_name);
          update("bs_period_label", bs_period_label);
          update("month_year", ad_month_year);
          update("bs_ad_start", ad_start);
          update("bs_ad_end", ad_end);
        }} />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <F label="Promoter ID (UUID)" required><Input value={form.promoter_id || ""} onChange={(e) => update("promoter_id", e.target.value)} data-testid="fin-target-promoter-id" /></F>
        <F label="Sales Amount (NPR)" required><Input type="number" value={form.target_sales_amount || ""} onChange={(e) => update("target_sales_amount", e.target.value)} /></F>
        <F label="Quantity (optional)"><Input type="number" value={form.target_quantity || ""} onChange={(e) => update("target_quantity", e.target.value)} /></F>
      </div>
    </div>
  );
  if (kind === "slab") return (
    <div className="grid grid-cols-2 gap-2">
      <F label="Achievement % From" required><Input type="number" value={form.achievement_pct_from || ""} onChange={(e) => update("achievement_pct_from", e.target.value)} /></F>
      <F label="Achievement % To (optional)"><Input type="number" value={form.achievement_pct_to || ""} onChange={(e) => update("achievement_pct_to", e.target.value)} /></F>
      <F label="Payout % of earned incentive" required><Input type="number" value={form.payout_pct || ""} onChange={(e) => update("payout_pct", e.target.value)} /></F>
      <F label="Bonus Type">
        <Select value={form.bonus_type || "none"} onValueChange={(v) => update("bonus_type", v)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            <SelectItem value="fixed_amount">Fixed amount</SelectItem>
            <SelectItem value="percentage">Percentage</SelectItem>
          </SelectContent>
        </Select>
      </F>
      {form.bonus_type !== "none" && (
        <F label="Bonus Value"><Input type="number" value={form.bonus_value || ""} onChange={(e) => update("bonus_value", e.target.value)} /></F>
      )}
    </div>
  );
  if (kind === "bonus_rule") return (
    <div className="space-y-2">
      <F label="Name" required><Input value={form.name || ""} onChange={(e) => update("name", e.target.value)} /></F>
      <F label="Description"><Textarea rows={2} value={form.description || ""} onChange={(e) => update("description", e.target.value)} /></F>
      <F label="Bonus Amount (NPR)" required><Input type="number" value={form.bonus_amount || ""} onChange={(e) => update("bonus_amount", e.target.value)} /></F>
    </div>
  );
  if (kind === "scheme") return (
    <div className="grid grid-cols-2 gap-2">
      <F label="Name" required><Input value={form.name || ""} onChange={(e) => update("name", e.target.value)} /></F>
      <F label="Target Type" required>
        <Select value={form.target_type || "monthly"} onValueChange={(v) => update("target_type", v)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="monthly">Monthly</SelectItem>
            <SelectItem value="quarterly">Quarterly</SelectItem>
            <SelectItem value="festival">Festival</SelectItem>
          </SelectContent>
        </Select>
      </F>
      <F label="Start Date" required><Input type="date" value={form.start_date || ""} onChange={(e) => update("start_date", e.target.value)} /></F>
      <F label="End Date" required><Input type="date" value={form.end_date || ""} onChange={(e) => update("end_date", e.target.value)} /></F>
      <F label="Target Amount" required><Input type="number" value={form.target_amount || ""} onChange={(e) => update("target_amount", e.target.value)} /></F>
      <F label="Target Quantity"><Input type="number" value={form.target_quantity || ""} onChange={(e) => update("target_quantity", e.target.value)} /></F>
    </div>
  );
  return null;
}

function F({ label, required, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] uppercase font-semibold text-slate-600">{label}{required && <span className="text-rose-600 ml-1">*</span>}</Label>
      {children}
    </div>
  );
}

function AuditDialog({ record, onClose }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!record) return;
    api.get(`/promoter/financial/_audit/${record.id}`)
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]));
  }, [record]);
  if (!record) return null;
  return (
    <Dialog open={!!record} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="fin-audit-dialog">
        <DialogHeader><DialogTitle>Audit trail · {record.id.slice(0, 8)}</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {rows.length === 0 && <div className="text-sm text-slate-400">No audit entries.</div>}
          {rows.map((r) => (
            <div key={r.id} className="border border-slate-100 rounded p-2 text-sm">
              <div className="font-medium">{r.action}</div>
              <div className="text-xs text-slate-600">by {r.actor_name} ({r.actor_role}) · {(r.ts || "").slice(0, 19)}</div>
              {r.remarks && <div className="text-xs text-slate-500 mt-1">Remarks: {r.remarks}</div>}
              {r.rejection_reason && <div className="text-xs text-rose-700 mt-1">Rejected: {r.rejection_reason}</div>}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
